import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useHandleActionItem } from "../../../hooks/useHandleActionItem.js";
import InputText from "../../../components/forms/InputText.jsx";
import { apiUrl } from "../../../config/config.js";

const Toast = Swal.mixin({
	toast: true,
	position: "top-end",
	showConfirmButton: false,
	timer: 3000,
	timerProgressBar: true,
	didOpen: (toast) => {
		toast.onmouseenter = Swal.stopTimer;
		toast.onmouseleave = Swal.resumeTimer;
	},
});

const reverseTranslateKeys = (data) => {
	const reverseMap = {
		Correo: "email",
		Nombre: "name",
		País: "country",
		Teléfono: "phone",
		Rol: "role",
		ID: "id",
		Identificación: "identification",
		"Precio Dibujos": "drawingsPrice",
		"Precio Estrategias": "strategyPrice",
		"Imagen de perfil": "profileImage",
		"Drawer Name": "drawerName",
		Estado: "status",
		"Estado Dibujos": "statusDrawings",
		"Desde cuándo copia": "copySince",
		"Descripción Copy": "copyDescription",
		Instagram: "instagram",
		Telegram: "telegram",
		IB: "ib",
		"IB Parent": "ibParent",
		"Demo Acc": "demoAcc",
		"Real Acc": "realAcc",
		"Demo Token": "demoToken",
		"Real Token": "realToken",
		"CopyTrader Activado": "copyTrader",
		"CopyTrader MT5 Activado": "copyTraderMT5",
		"Enviar señales a App": "sendToApp",
		"Enviar señales a Telegram": "sendToTelegram",
		"Paid Options": "paidOptions",
		"Paid Drawings": "paidDrawings",
		Drawings: "drawings",
		Creado: "createdAt",
	};

	const normalized = {};
	for (const key in data) {
		const normalizedKey = reverseMap[key] || key;
		normalized[normalizedKey] = data[key];
	}
	return normalized;
};


function UserDetails() {
	const location = useLocation();
	const navigate = useNavigate();
	const locationType = location.state?.type || "";
	const data = useMemo(() => location.state?.data || [], [location.state?.data]);
	console.log("DETILS DATA:", data);

	const [user, setUser] = useState({});
	const { handleDelete, handleSubmit } = useHandleActionItem();

	useEffect(() => {
		if (locationType === "edit" && data) {
			setUser(reverseTranslateKeys(data));
		}
	}, [locationType, data]);

	const validateForm = () => {
		if (!user.name || !user.email || !user.role) {
			Toast.fire({
				icon: "error",
				title: "Los campos obligatorios no pueden estar vacíos",
				background: "#1e2836",
			});
			return false;
		}
		return true;
	};

	const handleInputChange = (field, value) => {
		setUser((prev) => ({ ...prev, [field]: value }));
	};

	const handleSubmitForm = async (event) => {
		event.preventDefault();
		if (!validateForm()) return;

		try {
			if (locationType === "add") {
				const response = await fetch(apiUrl + "/register", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(user),
				});
				const responseData = await response.json();
				if (responseData.ok) {
					Toast.fire({ icon: "success", title: responseData.message, background: "#1e2836", color: "#fff" });
					window.history.back();
					return;
				} else {
					Toast.fire({ icon: "error", title: responseData.message, background: "#1e2836", color: "#fff" });
				}
			} else {
				await handleSubmit("update", "users", user);
			}
			navigate("/users");
		} catch (error) {
			console.log("ERROR", error);
		}
	};

	const handleDeleteForm = async (event) => {
		event.preventDefault();
		await handleDelete(user.id, "users", true);
	};

	return (
		<div>
			<form onSubmit={handleSubmitForm}>
				<h1 className="text-center">{locationType === "add" ? "Agregar Usuario" : "Actualizar Usuario"}</h1>

				<InputText label="Nombre" value={user.name || ""} setValue={(v) => handleInputChange("name", v)} />
				<InputText
					label="Correo"
					value={user.email || ""}
					setValue={(v) => handleInputChange("email", v)}
					disabled={locationType === "edit"}
				/>
				<InputText label="País" value={user.country || ""} setValue={(v) => handleInputChange("country", v)} />
				<InputText label="Rol" value={user.role || ""} setValue={(v) => handleInputChange("role", v)} />

				<InputText
					label="Precio Dibujos"
					value={user.drawingsPrice || ""}
					setValue={(v) => handleInputChange("drawingsPrice", v)}
					type="number"
				/>
				<InputText
					label="Precio Estrategias"
					value={user.strategyPrice || ""}
					setValue={(v) => handleInputChange("strategyPrice", v)}
					type="number"
				/>
				<InputText label="Instagram" value={user.instagram || ""} setValue={(v) => handleInputChange("instagram", v)} />
				<InputText label="Telegram" value={user.telegram || ""} setValue={(v) => handleInputChange("telegram", v)} />
				<InputText label="Descripción Copy" value={user.copyDescription || ""} setValue={(v) => handleInputChange("copyDescription", v)} />
				<InputText label="Desde cuándo copia" value={user.copySince || ""} setValue={(v) => handleInputChange("copySince", v)} />
				<InputText label="Estado" value={user.status || ""} setValue={(v) => handleInputChange("status", v)} />
				<InputText label="IB" value={user.ib || ""} setValue={(v) => handleInputChange("ib", v)} />
				<InputText label="IB Parent" value={user.ibParent || ""} setValue={(v) => handleInputChange("ibParent", v)} />
				<InputText label="Paid Options (JSON)" value={user.paidOptions || ""} setValue={(v) => handleInputChange("paidOptions", v)} />
				<InputText label="Paid Drawings (JSON)" value={user.paidDrawings || ""} setValue={(v) => handleInputChange("paidDrawings", v)} />

				{/* Checkboxes */}
				<div className="form-check mb-2">
					<input
						className="form-check-input"
						type="checkbox"
						checked={!!user.sendToTelegram}
						onChange={(e) => handleInputChange("sendToTelegram", e.target.checked)}
					/>
					<label className="form-check-label">Enviar señales a Telegram</label>
				</div>

				<div className="form-check mb-2">
					<input
						className="form-check-input"
						type="checkbox"
						checked={!!user.sendToApp}
						onChange={(e) => handleInputChange("sendToApp", e.target.checked)}
					/>
					<label className="form-check-label">Enviar señales a App</label>
				</div>

				<div className="form-check mb-2">
					<input
						className="form-check-input"
						type="checkbox"
						checked={!!user.copyTrader}
						onChange={(e) => handleInputChange("copyTrader", e.target.checked)}
					/>
					<label className="form-check-label">CopyTrader Activado</label>
				</div>

				<div className="form-check mb-4">
					<input
						className="form-check-input"
						type="checkbox"
						checked={!!user.copyTraderMT5}
						onChange={(e) => handleInputChange("copyTraderMT5", e.target.checked)}
					/>
					<label className="form-check-label">CopyTrader MT5 Activado</label>
				</div>
				<hr />
				{/* <p>
					<b>Demo Token:</b> {maskToken(user.demoToken)}
				</p> */}
				<p>
					<b>Demo Acc:</b> {user.demoAcc}
				</p>
				{/* <p>
					<b>Real Token:</b> {maskToken(user.realToken)}
				</p> */}
				<p>
					<b>Real Acc:</b> {user.realAcc}
				</p>

				<hr />

				<hr />
				<div className="mb-3 d-flex justify-content-around ">
					<button type="submit" className="btn btn-outline-theme btn-lg">
						{locationType === "add" ? "Agregar" : "Actualizar"}
					</button>
					<button onClick={() => navigate("/users")} className="btn btn-outline-theme btn-lg">
						Atrás
					</button>
					{locationType === "edit" && (
						<button onClick={handleDeleteForm} className="btn btn-outline-danger btn-lg">
							Eliminar
						</button>
					)}
				</div>
			</form>
		</div>
	);
}

export default UserDetails;
